import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Form, Spinner, Alert, Badge,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import dayjs from 'dayjs';
import _ from 'lodash';

import BookingService from '../../Services/BookingService';
import useModalState from '../../Hooks/useModalState';
import useCsvExport from '../../Hooks/useCsvExport';
import OrderInputModal from '../Orders/Components/OrderInputModal';
import OperatorInputModal from '../Orders/Components/OperatorInputModal';
import DriverInputModal from '../Orders/Components/DriverInputModal';
import EventTimeline from './Components/EventTimeline';
import PassengerSearch from './Components/PassengerSearch';
import GroupEditorModal from './Components/GroupEditorModal';
import GroupFlightDetailsModal from './Components/GroupFlightDetailsModal';
import BatchOperatorInputModal from './Components/BatchOperatorInputModal';
import BatchDriverInputModal from './Components/BatchDriverInputModal';

function Events() {
  const navigate = useNavigate();

  // Event selection state
  const [eventNames, setEventNames] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  // Event bookings state
  const [bookings, setBookings] = useState([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [error, setError] = useState('');

  // Group suggestions state
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Modal states
  const orderInputModal = useModalState();
  const groupEditorModal = useModalState();
  const operatorModal = useModalState();
  const driverModal = useModalState();
  const flightDetailsModal = useModalState();
  const batchOperatorModal = useModalState();
  const batchDriverModal = useModalState();

  // Selected bookings for bulk operations
  const [selectedBookings, setSelectedBookings] = useState([]);

  // Export functionality
  const { exportCsv, isExporting } = useCsvExport('/bookings', {
    event_name: selectedEvent,
    event_bookings_only: true,
  });

  // Fetch available event names
  const fetchEventNames = useCallback(async () => {
    setIsLoadingEvents(true);
    try {
      const names = await BookingService.getEventNames();
      setEventNames(names);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching event names:', err);
      setError('Failed to load event names');
    } finally {
      setIsLoadingEvents(false);
    }
  }, []);

  // Fetch bookings for selected event
  const fetchEventBookings = useCallback(async () => {
    if (!selectedEvent) {
      setBookings([]);
      return;
    }

    setIsLoadingBookings(true);
    setError('');
    try {
      const result = await BookingService.getEventBookings(selectedEvent);
      setBookings(result.bookings || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching event bookings:', err);
      setError('Failed to load event bookings');
    } finally {
      setIsLoadingBookings(false);
    }
  }, [selectedEvent]);

  // Fetch group suggestions
  const fetchGroupSuggestions = useCallback(async () => {
    if (!selectedEvent) {
      setGroupSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const suggestions = await BookingService.getEventGroupSuggestions(selectedEvent);
      setGroupSuggestions(suggestions);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching suggestions:', err);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [selectedEvent]);

  // Initial load
  useEffect(() => {
    fetchEventNames();
  }, [fetchEventNames]);

  // Load bookings when event changes
  useEffect(() => {
    fetchEventBookings();
    fetchGroupSuggestions();
    setSelectedBookings([]);
  }, [selectedEvent, fetchEventBookings, fetchGroupSuggestions]);

  // Handle event selection
  const handleEventChange = (selected) => {
    if (selected.length > 0) {
      const value = typeof selected[0] === 'string' ? selected[0] : selected[0].label || selected[0];
      setSelectedEvent(value);
    } else {
      setSelectedEvent('');
    }
  };

  // Handle booking selection toggle
  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings((prev) => (prev.includes(bookingId)
      ? prev.filter((id) => id !== bookingId)
      : [...prev, bookingId]));
  };

  // Select all bookings
  const selectAllBookings = () => {
    if (selectedBookings.length === bookings.length) {
      setSelectedBookings([]);
    } else {
      // eslint-disable-next-line no-underscore-dangle
      setSelectedBookings(bookings.map((b) => b._id));
    }
  };

  // Open group editor for selected bookings
  const openGroupEditor = (group = null) => {
    groupEditorModal.show({
      bookingIds: selectedBookings,
      currentGroup: group,
      eventName: selectedEvent,
    });
  };

  // Handle bulk group assignment
  const handleBulkGroupAssign = async (eventGroup) => {
    try {
      await BookingService.bulkUpdateEventGroup(selectedBookings, eventGroup);
      await fetchEventBookings();
      setSelectedBookings([]);
      groupEditorModal.hide();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error updating group:', err);
      setError('Failed to update group assignment');
    }
  };

  // Apply a group suggestion
  const applySuggestion = async (suggestion) => {
    try {
      await BookingService.bulkUpdateEventGroup(suggestion.bookingIds, suggestion.suggestedGroup);
      await fetchEventBookings();
      await fetchGroupSuggestions();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error applying suggestion:', err);
      setError('Failed to apply group suggestion');
    }
  };

  // Group bookings by event_group for timeline view
  const groupedBookings = _.groupBy(bookings, (b) => b.event_group || 'Ungrouped');

  // Count statistics
  const totalPassengers = bookings.reduce((sum, b) => sum + (b.passengers?.amount || 0), 0);
  const totalManifestPassengers = bookings.reduce(
    (sum, b) => sum + (b.passenger_manifest?.length || 0),
    0,
  );

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Event
              {' '}
              <span className="hero-highlight">Management</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage conference and event bookings with grouped pickups
            </p>
            <div className="hero-actions d-flex gap-2 flex-wrap">
              <Button
                onClick={fetchEventBookings}
                size="lg"
                disabled={!selectedEvent || isLoadingBookings}
              >
                <i className={`fa fa-sync ${isLoadingBookings ? 'fa-spin' : ''} me-2`} />
                Refresh
              </Button>
              <Button
                onClick={exportCsv}
                variant="success"
                size="lg"
                disabled={!selectedEvent || isExporting}
              >
                <i className={`fa fa-download ${isExporting ? 'fa-spin' : ''} me-2`} />
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-calendar-alt fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      {/* Event Selector */}
      <div className="mb-4 p-3 bg-light rounded border">
        <div className="row align-items-center">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label className="fw-bold">
                <i className="fa fa-calendar me-2" />
                Select Event
              </Form.Label>
              <Typeahead
                id="event-selector"
                allowNew
                newSelectionPrefix="Create new event: "
                placeholder={isLoadingEvents ? 'Loading events...' : 'Choose or create an event...'}
                options={eventNames}
                selected={selectedEvent ? [selectedEvent] : []}
                onChange={handleEventChange}
                disabled={isLoadingEvents}
                size="lg"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            {selectedEvent && (
              <div className="d-flex gap-3 mt-3 mt-md-4">
                <div className="text-center">
                  <div className="fs-3 fw-bold text-primary">{bookings.length}</div>
                  <small className="text-muted">Bookings</small>
                </div>
                <div className="text-center">
                  <div className="fs-3 fw-bold text-success">{totalPassengers}</div>
                  <small className="text-muted">Passengers</small>
                </div>
                <div className="text-center">
                  <div className="fs-3 fw-bold text-info">{Object.keys(groupedBookings).length}</div>
                  <small className="text-muted">Groups</small>
                </div>
                {totalManifestPassengers > 0 && (
                  <div className="text-center">
                    <div className="fs-3 fw-bold text-warning">{totalManifestPassengers}</div>
                    <small className="text-muted">Manifest</small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoadingBookings && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-3">Loading event bookings...</p>
        </div>
      )}

      {/* No Event Selected */}
      {!selectedEvent && !isLoadingEvents && (
        <div className="text-center py-5 bg-light rounded">
          <i className="fa fa-calendar-alt fa-4x text-muted mb-3" />
          <h4 className="text-muted">Select an event to view bookings</h4>
          <p className="text-muted">
            Choose an event from the dropdown above to see its bookings and manage groups.
          </p>
        </div>
      )}

      {/* Event Content */}
      {selectedEvent && !isLoadingBookings && (
        <div className="row">
          {/* Main Content - Timeline */}
          <div className="col-lg-8">
            {/* Bulk Actions Bar */}
            {selectedBookings.length > 0 && (
              <div className="mb-3 p-3 bg-primary text-white rounded d-flex align-items-center justify-content-between">
                <span>
                  <i className="fa fa-check-square me-2" />
                  {selectedBookings.length}
                  {' '}
                  booking(s) selected
                </span>
                <div className="d-flex gap-2">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => openGroupEditor()}
                  >
                    <i className="fa fa-users me-1" />
                    Assign Group
                  </Button>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setSelectedBookings([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Timeline View */}
            <EventTimeline
              groupedBookings={groupedBookings}
              selectedBookings={selectedBookings}
              onToggleSelection={toggleBookingSelection}
              onSelectAll={selectAllBookings}
              onEditBooking={(booking) => orderInputModal.show(booking)}
              onAssignOperator={(booking) => operatorModal.show(booking)}
              onAssignDriver={(booking) => driverModal.show(booking)}
              onShowFlightDetails={(groupName, groupBookings) => (
                flightDetailsModal.show({ groupName, bookings: groupBookings })
              )}
              onBatchAssignOperator={(groupBookings) => batchOperatorModal.show(groupBookings)}
              onBatchAssignDriver={(groupBookings) => batchDriverModal.show(groupBookings)}
              onRefresh={fetchEventBookings}
              // The link badge. This screen has no filter set of its own —
              // it is scoped to one event — and a booking's links routinely
              // point outside that event, so it hands the question to the
              // screen that can answer it. Orders keeps its filters in the
              // URL (useUrlViewState), so the same `linked_booking_ids`
              // filter the Orders table applies in place is reachable here
              // as an address. Without this prop the badge renders for
              // nobody — the timeline hides it unless a handler is passed.
              onSearchLinkedBookings={(booking) => navigate(
                `/orders?filters=${encodeURIComponent(JSON.stringify({
                  // eslint-disable-next-line no-underscore-dangle
                  linked_booking_ids: booking._id,
                }))}`,
              )}
            />
          </div>

          {/* Sidebar - Search & Suggestions */}
          <div className="col-lg-4">
            {/* Passenger Search */}
            <PassengerSearch
              eventName={selectedEvent}
              onBookingClick={(booking) => orderInputModal.show(booking)}
            />

            {/* Group Suggestions */}
            {groupSuggestions.length > 0 && (
              <div className="card mb-3">
                <div className="card-header bg-warning-subtle">
                  <i className="fa fa-lightbulb me-2" />
                  Group Suggestions
                  {isLoadingSuggestions && <Spinner size="sm" className="ms-2" />}
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {groupSuggestions.slice(0, 5).map((suggestion, idx) => (
                      <div
                        key={`suggestion-${idx + 1}`}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div className="fw-bold">{suggestion.suggestedGroup}</div>
                          <small className="text-muted">
                            {suggestion.bookingIds.length}
                            {' '}
                            bookings -
                            {' '}
                            {dayjs(suggestion.pickupTime).format('MMM D, HH:mm')}
                          </small>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => applySuggestion(suggestion)}
                        >
                          Apply
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats by Group */}
            <div className="card">
              <div className="card-header">
                <i className="fa fa-chart-pie me-2" />
                Groups Overview
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {Object.entries(groupedBookings).map(([group, groupBookings]) => (
                    <div
                      key={group}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <Badge
                          bg={group === 'Ungrouped' ? 'secondary' : 'primary'}
                          className="me-2"
                        >
                          {groupBookings.length}
                        </Badge>
                        {group}
                      </div>
                      <small className="text-muted">
                        {groupBookings.reduce((sum, b) => sum + (b.passengers?.amount || 0), 0)}
                        {' '}
                        pax
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Input Modal */}
      <OrderInputModal
        show={orderInputModal.isVisible}
        handleClose={orderInputModal.hide}
        fetchOrders={fetchEventBookings}
        originalOrder={orderInputModal.selectedObject || {}}
      />

      {/* Group Editor Modal */}
      <GroupEditorModal
        show={groupEditorModal.isVisible}
        onHide={groupEditorModal.hide}
        data={groupEditorModal.selectedObject}
        onAssign={handleBulkGroupAssign}
        existingGroups={Object.keys(groupedBookings).filter((g) => g !== 'Ungrouped')}
      />

      {/* Operator Assignment Modal */}
      <OperatorInputModal
        show={operatorModal.isVisible}
        handleClose={operatorModal.hide}
        fetchOrders={fetchEventBookings}
        originalOrder={operatorModal.selectedObject || {}}
      />

      {/* Driver Assignment Modal */}
      <DriverInputModal
        show={driverModal.isVisible}
        handleClose={driverModal.hide}
        fetchOrders={fetchEventBookings}
        originalOrder={driverModal.selectedObject || {}}
      />

      {/* Group Flight Details Modal */}
      <GroupFlightDetailsModal
        show={flightDetailsModal.isVisible}
        onHide={flightDetailsModal.hide}
        groupName={flightDetailsModal.selectedObject?.groupName || ''}
        bookings={flightDetailsModal.selectedObject?.bookings || []}
      />

      {/* Batch Operator Assignment Modal */}
      <BatchOperatorInputModal
        show={batchOperatorModal.isVisible}
        handleClose={batchOperatorModal.hide}
        fetchOrders={fetchEventBookings}
        bookings={batchOperatorModal.selectedObject || []}
      />

      {/* Batch Driver Assignment Modal */}
      <BatchDriverInputModal
        show={batchDriverModal.isVisible}
        handleClose={batchDriverModal.hide}
        fetchOrders={fetchEventBookings}
        bookings={batchDriverModal.selectedObject || []}
      />
    </>
  );
}

export default Events;
